import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { useNotification } from '../../../contexts/NotificationContext';
import { contentService } from '../../../services/api';
import { useSettings } from '../../../contexts/SettingsContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const Description = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const SectionList = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const SectionItem = styled.div<{ $isDragging: boolean; $enabled: boolean }>`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: ${({ $isDragging, $enabled }) => 
    $isDragging ? '#f3f4f6' : $enabled ? 'white' : '#f9fafb'};
  opacity: ${({ $enabled }) => $enabled ? 1 : 0.6};
  transition: all 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f9fafb;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const DragHandle = styled.div`
  color: #9ca3af;
  cursor: grab;
  font-size: 1.2rem;
  
  &:active {
    cursor: grabbing;
  }
`;

const SectionInfo = styled.div`
  flex: 1;
`;

const SectionName = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

const SectionDescription = styled.p`
  margin: 0.25rem 0 0 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleSlider = styled.span<{ $checked: boolean }>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ $checked }) => $checked ? '#10b981' : '#d1d5db'};
  transition: 0.4s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: ${({ $checked }) => $checked ? '26px' : '3px'};
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const SaveButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 2rem;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

interface HomepageSection {
  id: string;
  name: string;
  enabled: boolean;
  order: number;
  description?: string;
}

const sectionDescriptions = {
  hero: "Banner principal com título, busca e imagem de fundo",
  stats: "Números da empresa (imóveis, cidades, clientes, anos)",
  categories: "Categorias de imóveis em destaque com propriedades",
  properties: "Grid de imóveis em destaque",
  reviews: "Avaliações do Google Business (se configurado)"
};

const HomepageSettings: React.FC = () => {
  const [sections, setSections] = useState<HomepageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();
  const { refreshSettings } = useSettings();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await contentService.getSiteSettings();
      
      if (response.homepage_sections && Array.isArray(response.homepage_sections)) {
        setSections(response.homepage_sections);
      } else {
        // Se não houver configuração, criar padrão
        const defaultSections = [
          { id: "hero", name: "Hero/Banner", enabled: true, order: 1 },
          { id: "stats", name: "Estatísticas", enabled: true, order: 2 },
          { id: "categories", name: "Categorias em Destaque", enabled: true, order: 3 },
          { id: "properties", name: "Imóveis em Destaque", enabled: true, order: 4 },
          { id: "reviews", name: "Avaliações Google", enabled: true, order: 5 }
        ];
        setSections(defaultSections);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      showNotification('Erro ao carregar configurações das seções', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Atualizar ordem
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    setSections(updatedItems);
  };

  const handleToggle = (sectionId: string) => {
    setSections(prevSections => 
      prevSections.map(section => 
        section.id === sectionId 
          ? { ...section, enabled: !section.enabled }
          : section
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      await contentService.updateSiteSettings({
        homepage_sections: sections
      });
      
      // Forçar refresh das configurações no contexto
      await refreshSettings();
      
      showNotification('Configurações das seções salvas com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      showNotification('Erro ao salvar configurações das seções', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingMessage>Carregando configurações...</LoadingMessage>;
  }

  return (
    <Container>
      <Title>Configuração da Página Inicial</Title>
      <Description>
        Configure a ordem e visibilidade das seções da página inicial. 
        Arraste e solte para reordenar as seções, e use os switches para ativar/desativar.
      </Description>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections">
          {(provided: DroppableProvided) => (
            <SectionList {...provided.droppableProps} ref={provided.innerRef}>
              {sections.map((section, index) => (
                <Draggable key={section.id} draggableId={section.id} index={index}>
                  {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                    <SectionItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      $isDragging={snapshot.isDragging}
                      $enabled={section.enabled}
                    >
                      <SectionHeader>
                        <DragHandle {...provided.dragHandleProps}>
                          ⋮⋮
                        </DragHandle>
                        
                        <SectionInfo>
                          <SectionName>{section.name}</SectionName>
                          <SectionDescription>
                            {sectionDescriptions[section.id as keyof typeof sectionDescriptions] || 
                             'Seção personalizada'}
                          </SectionDescription>
                        </SectionInfo>
                        
                        <ToggleSwitch>
                          <ToggleInput
                            type="checkbox"
                            checked={section.enabled}
                            onChange={() => handleToggle(section.id)}
                          />
                          <ToggleSlider $checked={section.enabled} />
                        </ToggleSwitch>
                      </SectionHeader>
                    </SectionItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </SectionList>
          )}
        </Droppable>
      </DragDropContext>

      <SaveButton onClick={handleSave} disabled={saving}>
        {saving ? 'Salvando...' : 'Salvar Configurações'}
      </SaveButton>
    </Container>
  );
};

export default HomepageSettings;
